import type { ApiEnvelope } from '@/api/errors';
import { BaseService } from './BaseService';

/** Конфигурация «Интеграции» перевозчика, как её отдаёт бэкенд (токен сервера наружу не возвращается). */
export interface IntegrationView {
  /** Адрес сервера перевозчика; null, если ещё не подключён. */
  readonly baseUrl: string | null;
  /** Ключ data1861 — бэкенд отдаёт его полностью на каждом чтении (в UI маскируем). null до подключения. */
  readonly apiKey: string | null;
  /** Токен сервера задан? Само значение write-only и наружу не отдаётся. */
  readonly tokenConfigured: boolean;
}

/** Тело сохранения подключения. Токен передаётся открытым текстом (бэкенд шифрует при записи). */
export interface IntegrationSaveRequest {
  readonly baseUrl: string;
  readonly authToken: string;
}

// «Интеграция» перевозчика: подключение его сервера + ключ data1861_api_key. Эндпоинты trucking-service
// напрямую (/trucking/v1/integration); требуют роль CARRIER и аттестацию организации (иначе 403).
class TruckingIntegrationService extends BaseService {
  /** Текущая конфигурация. GET — идемпотентно. Пусто (baseUrl/apiKey=null, tokenConfigured=false), если не подключено. */
  async getIntegration(): Promise<IntegrationView> {
    const response = await this.request<ApiEnvelope<IntegrationView>>(
      { method: 'get', url: '/trucking/v1/integration' },
      { idempotent: true },
    );
    return this.unwrap(response.data, response.status);
  }

  /** Сохранить подключение (baseUrl + токен). Первое сохранение подключает и генерирует ключ. Мутация — без повтора. */
  async saveIntegration(payload: IntegrationSaveRequest): Promise<IntegrationView> {
    const response = await this.request<ApiEnvelope<IntegrationView>>(
      { method: 'put', url: '/trucking/v1/integration', data: payload },
      { retry: false },
    );
    return this.unwrap(response.data, response.status);
  }

  /** Ротация ключа data1861 (старый мгновенно недействителен). 422, если ещё не подключено. Мутация — без повтора. */
  async rotateApiKey(): Promise<string> {
    const response = await this.request<ApiEnvelope<{ apiKey: string }>>(
      { method: 'post', url: '/trucking/v1/integration/api-key' },
      { retry: false },
    );
    return this.unwrap(response.data, response.status).apiKey;
  }
}

export const truckingIntegrationService = new TruckingIntegrationService();
